import { Struct } from 'contexts/structs/types/types'

export type VisibleStructTransition<T extends Record<string, any>> =
  | { type: 'none' }
  | { type: 'show'; struct: Struct<T> }
  | { type: 'update'; struct: Struct<T> }
  | { type: 'clear' }

/**
 * Pure transition when the condition-filtered struct list changes.
 * If the visible struct no longer matches, show the next eligible struct or hide.
 */
export const resolveVisibleStructTransition = <T extends Record<string, any>>({
  structsToDisplay,
  currentItem,
}: {
  structsToDisplay: Struct<T>[]
  currentItem: Struct<T> | undefined
}): VisibleStructTransition<T> => {
  if (currentItem) {
    const latest = structsToDisplay.find((struct) => struct.key === currentItem.key)
    if (latest) {
      return { type: 'update', struct: latest }
    }

    if (structsToDisplay.length > 0) {
      return { type: 'show', struct: structsToDisplay[0] }
    }

    return { type: 'clear' }
  }

  if (structsToDisplay.length > 0) {
    return { type: 'show', struct: structsToDisplay[0] }
  }

  return { type: 'none' }
}

/**
 * Promo dialogs only: if a higher-priority struct appears before the current one and has not
 * been shown this session, switch to it immediately.
 */
export const findHigherPriorityUnshownStruct = <T extends Record<string, any>>({
  structsToDisplay,
  currentKey,
  isShownThisSession,
}: {
  structsToDisplay: Struct<T>[]
  currentKey: string
  isShownThisSession: (structKey: string) => boolean
}): Struct<T> | undefined => {
  for (const struct of structsToDisplay) {
    if (struct.key === currentKey) {
      break
    }
    if (!isShownThisSession(struct.key)) {
      return struct
    }
  }
  return undefined
}
